import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import * as Icons from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { 
  Server, 
  Globe, 
  Shield, 
  Zap, 
  HardDrive, 
  Wifi, 
  Users, 
  Clock,
  CheckCircle,
  X,
  Search,
  TrendingUp,
  Star,
  Database,
  Monitor,
  Cpu,
  HardDriveIcon,
  Mail,
  Lock,
  Award,
  Headphones,
  Rocket,
  Settings,
  Cloud,
  Code,
  Palette,
  ShoppingCart,
  Building,
  Crown,
  ArrowRight,
  Package,
  BarChart3,
  Smartphone,
  ChevronDown,
  ChevronUp,
  MessageCircle,
  HelpCircle,
  BookOpen
} from 'lucide-react';
import { API_URLS } from '../../config/api';
import { formatPrice, safeParseFloat } from '../../utils/format';
import { API_CONFIG } from '../../config/api';
import MediaDisplay from '../MediaDisplay';

const DEFAULT_CONTENT = {
  hero: {
    badge: 'Sınırsız Hosting',
    title: 'Sınırsız Hosting ile',
    highlight_text: 'Sınırsız Güç!',
    title_suffix: '',
    description:
      'Sınırsız disk alanı, sınırsız trafik, sınırsız e-posta hesabı ve sınırsız veritabanı ile web sitenizi sınırsız bir şekilde büyütün!',
    bullets: [
      'Sınırsız Disk Alanı',
      'Sınırsız Trafik',
      'Sınırsız E-Posta',
      'Sınırsız Veritabanı',
      '%99.9 Uptime Garantisi',
      'Ücretsiz SSL Sertifikası'
    ],
    image_url: 'https://cdn.natrocdn.com/ResourceFiles/v1/images/hosting/unlimited-hosting.png'
  },
  packages_section: {
    title: 'Sınırsız Hosting Paketleri',
    description: 'İhtiyacınıza uygun sınırsız hosting paketini seçin ve hemen başlayın'
  },
  management_section: {
    title: 'Ayrıcalıklı Hosting Yönetim Özellikleri',
    description: 'Sınır Tanımayanlar İçin Yepyeni Bir Hosting Deneyimi'
  },
  management_features: [
    {
      icon: 'Globe',
      title: 'Sınırsız Web Sitesi Barındırma',
      description:
        'Sınırsız web sitesi açma özelliği sayesinde tüm web sayfalarınızı tek bir hosting altında toplayarak yönetebilirsiniz.'
    },
    {
      icon: 'HardDrive',
      title: 'Sınırsız Barındırma Özellikleri',
      description:
        'Sınırsız hosting paketleri ile birlikte Disk, Trafik, E-Posta, Veritabanı ve Subdomain gibi tüm barındırma özellikleri SINIRSIZ.'
    },
    {
      icon: 'Settings',
      title: 'cPanel / Plesk Kontrol Paneli',
      description: 'Kullanıcı dostu kontrol panelleri ile hosting yönetimi artık çok daha kolay ve pratik.'
    },
    {
      icon: 'Monitor',
      title: 'Multi Platform Hosting Teknolojisi',
      description:
        'Multi Platform Cloud Hosting, web sitenizi ister Linux ister Windows platformda barındırma fırsatı sunar ve ihtiyacınıza uygun platformu seçebilirsiniz.'
    },
    {
      icon: 'Zap',
      title: 'Tek Tıkla Uygulama Kurulumu',
      description:
        'Bir blog, haber sitesi ya da e-ticaret sitesi mi istiyorsunuz? Hazır uygulama aracı ile tek tıkla kolayca sitenizi kurun, hemen yayına başlayın.'
    },
    {
      icon: 'Headphones',
      title: 'Ücretsiz 7/24 Destek',
      description:
        "Alanında uzman Türkiye'nin en büyük hosting destek ekibi ile ihtiyaç duyduğunuzda, 3 farklı kanaldan 7/24 destek hizmeti sağlıyoruz."
    }
  ],
  performance_section: {
    title: 'En İyi Hosting Deneyimi, Jet Hızında Web Siteleri',
    description:
      'Yüksek Performanslı Sunucu Altyapısı ve Sınırsız Kaynaklar ile Benzersiz Hosting Deneyimini Keşfedin',
    features: [
      {
        icon: 'Zap',
        title: 'X10 Yüksek Performans',
        description:
          'Çoklu internet omurgası ve SSD Hosting altyapısı ile 10 kata kadar ekstra performans sunarak, ziyaretçilerinizin web sitenize jet hızında ulaşmasını sağlar.'
      },
      {
        icon: 'Shield',
        title: 'Antispam ve Antivirüs Koruması',
        description:
          'Web sitenizi antispam ve antivirüs koruması ile güvence altına alın. Tüm hosting sunucularında bulunan ve periyodik aralıklarla otomatik tarama yapan antispam ve antivirüs taraması ile güvendesiniz.'
      },
      {
        icon: 'Wifi',
        title: 'Ultra Hızlı Network Ağı',
        description:
          'Yüksek network kapasitesi ve yedekli fiber altyapısı sayesinde Türkiye içerisinde web sitelerinize 15 milisaniye ortalama yanıt süresi ile yüksek hızlı erişim sunar.'
      },
      {
        icon: 'Lock',
        title: 'Üstün Güvenlik Kalkanı',
        description:
          'Sitelerinizin güvenliğini sunucu ve uygulama düzeyinde yönetiyoruz. Tüm sunucu ve servisleri 7/24 izliyor, olağan dışı durumlarda anında müdahale ediyoruz.'
      },
      {
        icon: 'Server',
        title: 'Güçlü Sunucu Altyapısı',
        description:
          'Tüm hosting sunucuları, güçlü ve son teknoloji donanımlardan oluşmakta, tamamı yedekli sunucu altyapısı ile veri merkezinde barındırılır.'
      },
      {
        icon: 'Rocket',
        title: 'Jet Hızında Açılan Web Siteleri',
        description:
          'Tüm hosting paketlerinde ÜCRETSİZ olarak bulunan Litespeed teknolojisinin sunduğu HTTP cache teknolojisi ve önbellekleme ile web sitenizin sayfa yükleme hızı 5 kat artar.'
      }
    ]
  },
  common_features: {
    title: 'Ortak Özellikler',
    description: 'Tüm Hosting Paketlerinde Bulunan Standart Özellikler',
    items: [
      { icon: 'Settings', label: 'cPanel / Plesk\nKontrol Paneli' },
      { icon: 'Shield', label: '7/24 İzleme ve\nDDoS Koruması' },
      { icon: 'Zap', label: 'Tek Tıkla Yüzlerce\nOtomatik kurulum' },
      { icon: 'Award', label: '%99.9\nUptime Oranı' }
    ]
  },
  apps_section: {
    title: 'Yüzlerce Uygulama, Tek Tıkla Kuruluma Hazır',
    description: 'İhtiyacınıza Uygun Web Uygulamalarını Kolayca Kurun',
    apps: [
      { name: 'WordPress', icon: 'Code' },
      { name: 'Joomla', icon: 'Globe' },
      { name: 'Drupal', icon: 'Database' },
      { name: 'Magento', icon: 'ShoppingCart' },
      { name: 'OpenCart', icon: 'Package' },
      { name: 'PrestaShop', icon: 'Building' },
      { name: 'phpBB', icon: 'Users' },
      { name: 'MyBB', icon: 'MessageCircle' }
    ],
    paragraphs: [
      'Bir blog oluşturun, bir forum açın, wiki kullanın, kendi fotograf galerinizi yayınlayın veya bir e-ticaret sitesi ile online satış yapmaya başlayın.',
      'Yüzlerce hazır uygulama arasında seçim yaparak hızlı ve kolay bir şekilde sitenizi kurun. Üstelik herhangi bir teknik bilgiye ihtiyaç duymadan, tek tıkla ve dakikalar içinde web siteniz yayında.'
    ]
  },
  info_sections: [
    {
      highlight: 'Web Hosting',
      title: 'Nedir?',
      paragraphs: [
        'Web hosting, web sitelerinizin internette yayınlanması için gerekli olan sunucu alanı ve teknolojik altyapı hizmetidir. Bu hizmetin temel amacı, web sitenizin sürekli olarak yayında kalmasını sağlamaktır.',
        'İyi bir web hosting hizmeti, hızlı yüklenme süreleri, yüksek güvenlik önlemleri ve kesintisiz çalışma süresi sunarak web sitenizin performansını en üst düzeye çıkarır.'
      ],
      image_url: 'https://via.placeholder.com/600x400/10B981/FFFFFF?text=Web+Hosting'
    },
    {
      highlight: 'Web Hosting',
      title: 'Fiyatları',
      paragraphs: [
        'Genellikle depolama alanı, bant genişliği, e-posta hesapları ve müşteri desteği gibi faktörler, fiyatı belirlemede önemli rol oynar.',
        'Website Al olarak, uygun fiyatlı ve performans odaklı web hosting paketleri sunuyoruz. İhtiyacınıza göre esnek planlarımızla, her bütçeye uygun çözümler bulabilirsiniz.'
      ],
      card: {
        icon: 'TrendingUp',
        title: 'Web Hosting Fiyatları',
        description: [
          'Web hosting fiyatları, seçilen paket türüne, sunulan özelliklere ve hizmet sağlayıcısına göre değişiklik gösterebilir.'
        ],
        bullets: ['Depolama alanı', 'Bant genişliği', 'E-posta hesapları', 'Müşteri desteği']
      }
    }
  ],
  blogs_section: {
    title: 'Hosting ile İlgili Güncel İçerikler',
    description:
      'Hosting, Barındırma ve Kontrol Panelleri ile İlgili İçerikler, Faydalı Araçlar ve Resimli Teknik Anlatım İçerikleri'
  },
  faq: {
    title: 'Sıkça Sorulan Sorular',
    description: 'Hosting ile İlgili Sıkça Sorulan Sorular'
  },
  contact: {
    title: 'Hosting İhtiyacınız İçin Bize Ulaşın',
    description:
      'Profesyonel hosting çözümlerimiz hakkında detaylı bilgi almak ve size özel paket önerileri için ekibimizle iletişime geçin.',
    primary_button: { text: 'İletişime Geç', link: '/iletisim', icon: 'Mail' },
    secondary_button: { text: 'WhatsApp İletişim', action: 'whatsapp', icon: 'MessageCircle' },
    highlights: [
      { icon: 'Clock', title: '7/24 Destek', description: 'Kesintisiz teknik destek hizmeti' },
      { icon: 'Shield', title: 'Güvenli Altyapı', description: '%99.9 uptime garantisi' },
      { icon: 'Award', title: 'Uzman Ekip', description: 'Deneyimli hosting uzmanları' }
    ]
  }
};

const resolveIcon = (iconName, fallback = 'Server') => {
  if (!iconName && fallback && Icons[fallback]) {
    return Icons[fallback];
  }
  if (typeof iconName === 'string' && Icons[iconName]) {
    return Icons[iconName];
  }
  return Icons[fallback] || Server;
};

const HostingPage = () => {
  const [selectedTab, setSelectedTab] = useState('web-hosting');
  const [expandedFeatures, setExpandedFeatures] = useState({});
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [hostingPlans, setHostingPlans] = useState([]);
  const [hostingCategories, setHostingCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [whatsappNumber, setWhatsappNumber] = useState('905324404011');
  const [hostingBlogs, setHostingBlogs] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [faqsLoading, setFaqsLoading] = useState(false);
  const [contentReady, setContentReady] = useState(false);
  const [pageContent, setPageContent] = useState(null);

  // Fade-in animasyonu için
  useEffect(() => {
    const timer = setTimeout(() => {
      setContentReady(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await fetch(API_URLS.PAGE_CONTENT('sinirsiz-hosting'));
        const data = await res.json();
        if (data.status === 'success') {
          setPageContent(data.data);
        } else {
          setPageContent(null);
        }
      } catch (error) {
        console.error('Sınırsız hosting içeriği yüklenemedi:', error);
        setPageContent(null);
      }
    };

    fetchContent();
  }, []);

  // Fallback veriler (API'den veri gelmezse kullanılacak)
  const fallbackHostingPlans = [
    {
      id: 'web-hosting-basic',
      name: 'WH-Başlangıç Paket',
      type: 'Web Hosting',
      price: { monthly: 89, yearly: 890 },
      originalPrice: { monthly: 119, yearly: 1190 },
      discount: 25,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-50',
      icon: Server,
      popular: false,
      description: 'Küçük işletmelere uygun, hızlı ve güvenilir web hosting çözümü.',
      features: [
        { name: '1 Adet Web Sitesi', included: true },
        { name: '1 GB RAM', included: true },
        { name: '1 Core CPU', included: true },
        { name: '10 GB NVMe Disk alanı', included: true },
        { name: 'En Güncel PHP Sürümleri', included: true },
        { name: 'PHP Selector', included: true },
        { name: 'Sınırsız FTP Hesabı', included: true },
        { name: 'Sınırsız E-Posta Hesabı', included: true },
        { name: 'Sınırsız MySqL Veritabanı', included: true },
        { name: 'Sınırsız Aylık Ziyaretçi', included: true },
        { name: 'Sınırsız Ücretsiz SSL', included: true },
        { name: 'Sınırsız Trafik', included: true },
        { name: 'Ücretsiz 400+ Hazır Şablonlar', included: true }
      ],
      additionalFeatures: [
        { name: 'Ücretsiz Otomatik Site Taşıma', included: true },
        { name: 'Ücretsiz Haftalık Yedekleme', included: true },
        { name: '%99,99 Erişim Garantisi', included: true },
        { name: 'DNS Yönetimi', included: true },
        { name: 'Önbellek Yöneticisi', included: true },
        { name: 'Kötü Amaçlı Yazılım Tarayıcı', included: true },
        { name: 'Standart DDoS Koruması', included: true },
        { name: 'Web Uygulaması Güvenlik Duvarı', included: true },
        { name: 'Sınırsız Inode (Dosyalar ve Dizinler)', included: true },
        { name: '200 Adet Saatlik Mail Gönderimi', included: true }
      ],
      specs: { websites: '1', ram: '1 GB', cpu: '1 Core', storage: '10 GB NVMe' }
    }
  ];

  const fallbackHostingCategories = [
    {
      id: 'web-hosting',
      name: 'Web Hosting',
      icon: Server,
      description: 'Web hosting ile web sitelerinizi kolayca yönetin!',
      count: 3,
      color: 'bg-gradient-to-r from-blue-500 to-cyan-500'
    },
    {
      id: 'cpanel-hosting',
      name: 'cPanel Hosting',
      icon: Settings,
      description: 'cPanel hosting ile kolay web sitesi yönetimi sağlayın.',
      count: 0,
      color: 'bg-gradient-to-r from-green-500 to-emerald-500'
    },
    {
      id: 'windows-hosting',
      name: 'Windows Hosting',
      icon: Monitor,
      description: 'Windows hosting ile MSSQL ve .NET desteğiyle güçlü web çözümleri!',
      count: 0,
      color: 'bg-gradient-to-r from-purple-500 to-pink-500'
    },
    {
      id: 'reseller-hosting',
      name: 'Reseller Hosting',
      icon: Building,
      description: 'Reseller hosting ile kendi işinizi büyüterek kârlı fırsatlar yakalayın!',
      count: 0,
      color: 'bg-gradient-to-r from-orange-500 to-red-500'
    },
    {
      id: 'wordpress-hosting',
      name: 'WordPress Hosting',
      icon: Code,
      description: 'WordPress hosting ile web sitenizi hız ve güvenle yönetin.',
      count: 0,
      color: 'bg-gradient-to-r from-indigo-500 to-purple-500'
    },
    {
      id: 'enterprise-hosting',
      name: 'Kurumsal Hosting',
      icon: Crown,
      description: 'Kurumsal hosting ile altyapınızı güçlendirin ve işinizi büyütün.',
      count: 0,
      color: 'bg-gradient-to-r from-gray-500 to-slate-600'
    }
  ];

  // API'den veri çekme
  useEffect(() => {
    const fetchHostingData = async () => {
      try {
        setLoading(true);
        
        // API konfigürasyonunu kullan
        const baseUrl = API_CONFIG.BASE_URL;
        
        // Kategorileri getir
        const categoriesResponse = await fetch(API_URLS.HOSTING_CATEGORIES);
        if (categoriesResponse.ok) {
          const categoriesData = await categoriesResponse.json();
          if (Array.isArray(categoriesData)) {
            setHostingCategories(categoriesData);
          }
        }
        
        // Paketleri getir
        const packagesResponse = await fetch(API_URLS.HOSTING_PACKAGES);
        if (packagesResponse.ok) {
          const packagesData = await packagesResponse.json();
          if (Array.isArray(packagesData)) {
            const formattedPlans = packagesData.map(pkg => ({
              id: pkg.id,
              name: pkg.name,
              type: pkg.type,
              category: pkg.category, // Kategori alanını ekle
              price: { 
                monthly: safeParseFloat(pkg.price_monthly), 
                yearly: safeParseFloat(pkg.price_yearly) 
              },
              originalPrice: { 
                monthly: safeParseFloat(pkg.original_price_monthly), 
                yearly: safeParseFloat(pkg.original_price_yearly) 
              },
              discount: pkg.discount,
              color: pkg.color,
              bgColor: pkg.bg_color,
              icon: getIconComponent(pkg.icon),
              popular: pkg.is_popular == 1 || pkg.is_popular === "1" || pkg.is_popular === true,
              description: pkg.description,
              features: Array.isArray(pkg.features) ? pkg.features : [],
              additionalFeatures: Array.isArray(pkg.additional_features) ? pkg.additional_features : [],
              specs: pkg.specs || {}
            }));
            setHostingPlans(formattedPlans);
          }
        }

        // WhatsApp numarasını getir
        try {
          const settingsResponse = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=getCompanySettings`);
          if (settingsResponse.ok) {
            const settingsData = await settingsResponse.json();
            if (settingsData.status === 'success' && settingsData.data?.whatsapp) {
              setWhatsappNumber(settingsData.data.whatsapp);
            }
          }
        } catch (error) {
          console.error('WhatsApp numarası alınamadı:', error);
        }
      } catch (error) {
        console.error('Hosting verileri yüklenirken hata:', error);
        // Fallback verileri kullan
        setHostingPlans(fallbackHostingPlans);
        setHostingCategories(fallbackHostingCategories);
      } finally {
        setLoading(false);
      }
    };

    fetchHostingData();
  }, []);

  // Load FAQs for Hosting category
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setFaqsLoading(true);
        const res = await fetch(API_URLS.FAQ);
        const json = await res.json();
        if (!mounted) return;
        const categories = json.categories || [];
        const questions = json.questions || [];
        const hostingCat = categories.find(c => {
          const title = String(c.title || '').toLowerCase();
          return title === 'sınırsız hosting' || title === 'hosting';
        });
        if (hostingCat) {
          const list = questions
            .filter(q => q.category_id === hostingCat.id)
            .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
            .map(q => ({ question: q.question, answer: q.answer }));
          setFaqs(list);
        } else {
          setFaqs([]);
        }
      } catch (e) {
        if (mounted) setFaqs([]);
      } finally {
        if (mounted) setFaqsLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const mergedContent = useMemo(() => {
    const additional = pageContent?.additional_sections || {};
    const heroAdditional = additional.hero || {};
    const packagesAdditional = additional.packages_section || {};
    const managementSectionInfo = additional.management_section || {};
    const managementFeatures = Array.isArray(additional.management_features)
      ? additional.management_features
      : DEFAULT_CONTENT.management_features;
    const performanceAdditional = additional.performance_section || {};
    const performanceFeatures = Array.isArray(performanceAdditional.features)
      ? performanceAdditional.features
      : DEFAULT_CONTENT.performance_section.features;
    const commonAdditional = additional.common_features || {};
    const commonItems = Array.isArray(commonAdditional.items)
      ? commonAdditional.items
      : DEFAULT_CONTENT.common_features.items;
    const appsAdditional = additional.apps_section || {};
    const appsList = Array.isArray(appsAdditional.apps)
      ? appsAdditional.apps
      : DEFAULT_CONTENT.apps_section.apps;
    const appParagraphs = Array.isArray(appsAdditional.paragraphs)
      ? appsAdditional.paragraphs
      : DEFAULT_CONTENT.apps_section.paragraphs;
    const infoAdditional = Array.isArray(additional.info_sections)
      ? additional.info_sections
      : DEFAULT_CONTENT.info_sections;
    const blogsAdditional = additional.blogs_section || {};
    const faqAdditional = additional.faq || {};
    const contactAdditional = additional.contact || {};
    const contactHighlights = Array.isArray(contactAdditional.highlights)
      ? contactAdditional.highlights
      : DEFAULT_CONTENT.contact.highlights;

    return {
      hero: {
        ...DEFAULT_CONTENT.hero,
        ...heroAdditional,
        badge: pageContent?.hero_badge_text ?? heroAdditional.badge ?? DEFAULT_CONTENT.hero.badge,
        title: pageContent?.hero_title ?? heroAdditional.title ?? DEFAULT_CONTENT.hero.title,
        highlight_text: heroAdditional.highlight_text ?? DEFAULT_CONTENT.hero.highlight_text,
        title_suffix: heroAdditional.title_suffix ?? DEFAULT_CONTENT.hero.title_suffix,
        description:
          pageContent?.hero_description ??
          heroAdditional.description ??
          DEFAULT_CONTENT.hero.description,
        bullets: Array.isArray(heroAdditional.bullets)
          ? heroAdditional.bullets
          : DEFAULT_CONTENT.hero.bullets,
        image_url:
          heroAdditional.image_url ??
          pageContent?.hero_image_url ??
          DEFAULT_CONTENT.hero.image_url
      },
      packages_section: {
        ...DEFAULT_CONTENT.packages_section,
        ...packagesAdditional
      },
      management_section: {
        ...DEFAULT_CONTENT.management_section,
        ...managementSectionInfo
      },
      management_features: managementFeatures,
      performance_section: {
        ...DEFAULT_CONTENT.performance_section,
        ...performanceAdditional,
        features: performanceFeatures
      },
      common_features: {
        ...DEFAULT_CONTENT.common_features,
        ...commonAdditional,
        items: commonItems
      },
      apps_section: {
        ...DEFAULT_CONTENT.apps_section,
        ...appsAdditional,
        apps: appsList,
        paragraphs: appParagraphs
      },
      info_sections: infoAdditional.length ? infoAdditional : DEFAULT_CONTENT.info_sections,
      blogs_section: {
        ...DEFAULT_CONTENT.blogs_section,
        ...blogsAdditional
      },
      faq: {
        ...DEFAULT_CONTENT.faq,
        ...faqAdditional
      },
      contact: {
        ...DEFAULT_CONTENT.contact,
        ...contactAdditional,
        highlights: contactHighlights
      }
    };
  }, [pageContent]);

  const heroContent = mergedContent.hero;
  const packagesSection = mergedContent.packages_section;
  const managementSection =
    mergedContent.management_section || DEFAULT_CONTENT.management_section;
  const managementFeatures = Array.isArray(mergedContent.management_features)
    ? mergedContent.management_features
    : DEFAULT_CONTENT.management_features;
  const performanceSection = mergedContent.performance_section;
  const performanceFeatures = Array.isArray(performanceSection.features)
    ? performanceSection.features
    : DEFAULT_CONTENT.performance_section.features;
  const commonFeatures = mergedContent.common_features;
  const commonItems = Array.isArray(commonFeatures.items)
    ? commonFeatures.items
    : DEFAULT_CONTENT.common_features.items;
  const appsSection = mergedContent.apps_section;
  const infoSections = Array.isArray(mergedContent.info_sections)
    ? mergedContent.info_sections
    : DEFAULT_CONTENT.info_sections;
  const infoSectionOne = infoSections[0] || DEFAULT_CONTENT.info_sections[0];
  const infoSectionTwo = infoSections[1] || DEFAULT_CONTENT.info_sections[1];
  const infoSectionOneParagraphs = Array.isArray(infoSectionOne.paragraphs)
    ? infoSectionOne.paragraphs
    : DEFAULT_CONTENT.info_sections[0].paragraphs;
  const infoSectionTwoParagraphs = Array.isArray(infoSectionTwo.paragraphs)
    ? infoSectionTwo.paragraphs
    : DEFAULT_CONTENT.info_sections[1].paragraphs;
  const infoSectionTwoCard = infoSectionTwo.card || DEFAULT_CONTENT.info_sections[1].card;
  const infoSectionTwoCardDescription = Array.isArray(infoSectionTwoCard?.description)
    ? infoSectionTwoCard.description
    : DEFAULT_CONTENT.info_sections[1].card.description;
  const infoSectionTwoCardBullets = Array.isArray(infoSectionTwoCard?.bullets)
    ? infoSectionTwoCard.bullets
    : DEFAULT_CONTENT.info_sections[1].card.bullets;
  const blogsSection = mergedContent.blogs_section;
  const faqSection = mergedContent.faq;
  const contactSection = mergedContent.contact;

  // Load Hosting Blogs
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const fetchJson = async (url) => {
          const res = await fetch(url, { headers: { Accept: 'application/json' } });
          const raw = await res.text();
          try { return JSON.parse(raw); } catch { return { data: [] }; }
        };

        // Try server-side filtered fetch first
        let data = await fetchJson(`${API_URLS.BLOGS}&category=sinirsiz-hosting&limit=3`);
        let posts = Array.isArray(data) ? data : (data.blogs || data.data || []);

        // Fallback: fetch all then client-side filter by slug/name if empty
        if (!Array.isArray(posts) || posts.length === 0) {
          const allData = await fetchJson(API_URLS.BLOGS);
          const allPosts = Array.isArray(allData) ? allData : (allData.blogs || allData.data || []);
          posts = allPosts
            .filter(p => {
              const cat = String(p.category || '').toLowerCase();
              const slug = String(p.slug || '').toLowerCase();
              const catName = String(p.category_name || '').toLowerCase();
              return cat.includes('sinirsiz-hosting') || cat.includes('sınırsız hosting') || 
                     slug.includes('sinirsiz-hosting') || catName.includes('sınırsız hosting');
            })
            .slice(0, 3);
        }

        if (mounted) {
          setHostingBlogs(posts.map(p => ({
            id: p.id,
            title: p.title || '',
            excerpt: p.excerpt || p.summary || '',
            image: p.image || p.featured_image || '',
            slug: p.slug || '',
            category: p.category_name || p.category || 'Hosting'
          })));
        }
      } catch (e) {
        console.error('Error fetching hosting blogs:', e);
        if (mounted) setHostingBlogs([]);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // İkon component'ini döndüren yardımcı fonksiyon
  const getIconComponent = (iconName) => {
    const iconMap = {
      'Server': Server,
      'Settings': Settings,
      'Monitor': Monitor,
      'Package': Package,
      'Globe': Globe,
      'Shield': Shield,
      'Zap': Zap,
      'Crown': Crown,
      'TrendingUp': TrendingUp,
      'Building': Building,
      'Code': Code
    };
    return iconMap[iconName] || Server;
  };

  const toggleFeatures = (planId) => {
    setExpandedFeatures(prev => ({
      ...prev,
      [planId]: !prev[planId]
    }));
  };

  const HostingCard = ({ plan, index, showAllFeatures, onToggleFeatures }) => {
    const isExpanded = expandedFeatures[plan.id];
    const [isHovered, setIsHovered] = useState(false);
    
    // Tüm özellikleri birleştir
    const allFeatures = [
      ...(plan.features || []),
      ...(plan.additionalFeatures || [])
    ];
    
    const hasMoreFeatures = allFeatures.length > 4;
    
    return (
      <Card
        className={`relative border-0 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 cursor-pointer group ${
          plan.popular ? 'ring-4 ring-purple-200' : ''
        } bg-white flex flex-col`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          animationDelay: `${index * 0.1}s`,
          animation: 'fadeInUp 0.6s ease-out forwards'
        }}
      >
        {/* Popular Badge */}
        {plan.popular && (
          <div className="absolute top-0 transform -translate-x-1/2 -translate-y-1/2 z-10" style={{ left: '52%' }}>
            <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-1 text-sm font-bold animate-pulse" style={{ width: '150px' }}>
              <Crown className="w-4 h-4 mr-1" />
              EN POPÜLER
            </Badge>
          </div>
        )}

        {/* Background Gradient Animation */}
        <div className={`absolute inset-0 bg-gradient-to-br ${plan.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500 pointer-events-none`}></div>

        <CardHeader className="space-y-3 pb-4">
          <div className="space-y-3">
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${plan.color} flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
              <plan.icon className="w-6 h-6 text-white animate-pulse" />
            </div>
            
            <div className="space-y-1">
              <CardTitle className="text-lg font-bold text-gray-900 group-hover:text-emerald-700 transition-colors">
                {plan.name}
              </CardTitle>
              <p className="text-xs text-gray-600 leading-relaxed">{plan.description}</p>
            </div>
          </div>

          {/* Pricing */}
          <div className="space-y-1">
            <div className="flex items-baseline space-x-1">
              <span className="text-3xl font-bold text-gray-900">
                {formatPrice(plan.price.monthly, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </span>
              <span className="text-sm text-gray-600">
                /yıllık
              </span>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-400 line-through">
                {formatPrice(plan.originalPrice.monthly, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </span>
              <Badge variant="destructive" className="text-xs animate-bounce">
                %{plan.discount}
              </Badge>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex flex-col flex-1">
          {/* Features */}
          <div className="space-y-2 flex-1">
            <h4 className="font-semibold text-gray-800 text-sm">Özellikler:</h4>
            {(() => {
              // Eğer showAllFeatures false ise sadece ilk 4'ü göster
              const displayedFeatures = showAllFeatures ? allFeatures : allFeatures.slice(0, 4);
              
              return displayedFeatures.map((feature, idx) => {
                const isIncluded = feature.included !== false;
                return (
                  <div 
                    key={idx} 
                    className="flex items-center space-x-2 transition-all duration-300"
                    style={{
                      animationDelay: `${(index * 0.1) + (idx * 0.05)}s`,
                      animation: isHovered ? 'slideInRight 0.3s ease-out forwards' : 'none'
                    }}
                  >
                    {isIncluded ? (
                      <CheckCircle className="w-4 h-4 text-green-500 animate-pulse flex-shrink-0" />
                    ) : (
                      <X className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    )}
                    <span className={`text-xs font-medium ${isIncluded ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
                      {feature.name}
                    </span>
                  </div>
                );
              });
            })()}
            
            {/* Tüm Özellikleri Gör Button - Paket içinde */}
            {hasMoreFeatures && (
              <div className="mt-3 pt-2 border-t border-gray-200">
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleFeatures();
                  }}
                  variant="outline"
                  size="sm"
                  className="w-full border-emerald-600 text-emerald-600 hover:bg-emerald-50 text-xs"
                >
                  {showAllFeatures ? (
                    <>
                      <ChevronUp className="w-3 h-3 mr-1" />
                      Özellikleri Gizle
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-3 h-3 mr-1" />
                      Tüm Özellikleri Gör ({allFeatures.length - 4} daha)
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>

          {/* Specs ve Button - Her zaman en altta */}
          <div className="space-y-3 mt-auto pt-4">
            {/* Specs */}
            <div className="grid grid-cols-2 gap-2 border-t border-gray-200 pt-3">
              {Object.entries(plan.specs).map(([key, value], idx) => (
                <div key={key} className="text-center">
                  <p className="text-sm font-bold text-emerald-600">{value}</p>
                  <p className="text-xs text-gray-500 capitalize">{key}</p>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <Button 
              className={`w-full h-10 text-sm font-semibold transition-all duration-300 transform hover:scale-105 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white`}
            >
              <span>PLANI SEÇ</span>
              <ArrowRight className="w-3 h-3 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </CardContent>

        {/* Hover Animation Particles */}
        {isHovered && (
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/4 left-1/4 w-1 h-1 bg-emerald-400 rounded-full animate-ping"></div>
            <div className="absolute top-3/4 right-1/4 w-1 h-1 bg-green-400 rounded-full animate-ping" style={{animationDelay: '0.5s'}}></div>
            <div className="absolute top-1/2 right-1/3 w-1 h-1 bg-teal-400 rounded-full animate-ping" style={{animationDelay: '1s'}}></div>
          </div>
        )}
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section 
        className="bg-gradient-to-r from-emerald-600 to-green-600 text-white py-20"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Sol - İçerik */}
            <div className="space-y-6 text-center md:text-left">
              {heroContent.badge && (
                <Badge className="bg-emerald-500 text-white px-4 py-2 text-lg w-fit mx-auto md:mx-0">
                  {heroContent.badge}
                </Badge>
              )}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                {heroContent.title}{' '}
                {heroContent.highlight_text && (
                  <span className="text-yellow-300">{heroContent.highlight_text}</span>
                )}
                {heroContent.title_suffix ? ` ${heroContent.title_suffix}` : ''}
              </h1>
              {heroContent.description && (
                <p className="text-xl text-emerald-100 leading-relaxed">
                  {heroContent.description}
                </p>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 justify-items-center md:justify-items-start">
                {(heroContent.bullets || []).map((bullet, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="w-6 h-6 text-emerald-300" />
                    <span className="text-lg font-semibold">{bullet}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Sağ - Görsel */}
            <div className="hidden lg:block">
              <div className="relative">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 p-8">
                  <MediaDisplay
                    src={heroContent.image_url}
                    alt={heroContent.badge || 'Sınırsız Hosting'}
                    className="w-full rounded-xl"
                    videoProps={{ controls: true }}
                    fallbackSrc="https://via.placeholder.com/600x400/10B981/FFFFFF?text=Sınırsız+Hosting"
                    imgProps={{
                      onError: (e) => {
                        e.target.src = "https://via.placeholder.com/600x400/10B981/FFFFFF?text=Sınırsız+Hosting";
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hosting Paketleri */}
      <section 
        className="py-16 bg-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {packagesSection.title}
            </h2>
            {packagesSection.description && (
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {packagesSection.description}
              </p>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-emerald-600 mx-auto mb-3"></div>
                <p className="text-gray-600">Hosting paketleri yükleniyor...</p>
              </div>
            </div>
          ) : hostingPlans.length === 0 ? (
            <div className="text-center py-12">
              <Server className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">Paket Bulunamadı</h3>
              <p className="text-gray-500">Sınırsız hosting paketleri bulunamadı.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
                {hostingPlans.map((plan, index) => (
                  <HostingCard 
                    key={plan.id} 
                    plan={plan} 
                    index={index} 
                    showAllFeatures={showAllFeatures}
                    onToggleFeatures={() => setShowAllFeatures(!showAllFeatures)}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Ayrıcalıklı Hosting Yönetim Özellikleri */}
      <section 
        className="py-20 bg-gradient-to-r from-emerald-900 to-green-900"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">{managementSection.title}</h2>
            {managementSection.description && (
              <p className="text-xl text-emerald-200">{managementSection.description}</p>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {managementFeatures.map((feature, index) => {
              const Icon = resolveIcon(feature.icon, 'Globe');
              return (
                <div
                  key={index}
                  className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all text-center md:text-left"
                >
                  <div className="w-16 h-16 bg-emerald-500 rounded-lg flex items-center justify-center mb-4 mx-auto md:mx-0">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                  {feature.description && (
                    <p className="text-emerald-200">{feature.description}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* En İyi Hosting Deneyimi */}
      <section 
        className="py-20 bg-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {performanceSection.title}
            </h2>
            {performanceSection.description && (
              <p className="text-xl text-gray-600">
                {performanceSection.description}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {performanceFeatures.map((feature, index) => {
              const Icon = resolveIcon(feature.icon, 'Zap');
              return (
                <div key={index} className="flex flex-col md:flex-row items-center md:items-start space-y-4 md:space-y-0 md:space-x-6 text-center md:text-left">
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 bg-gradient-to-r from-emerald-500 to-green-500 rounded-lg flex items-center justify-center">
                      <Icon className="w-10 h-10 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                    {feature.description && (
                      <p className="text-gray-600">
                        {feature.description}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Ortak Özellikler */}
      <section 
        className="py-20 bg-gradient-to-r from-emerald-900 to-green-900"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 items-center">
            <div className="lg:col-span-1 text-center lg:text-left">
              <h2 className="text-3xl font-bold text-white mb-4">{commonFeatures.title}</h2>
              {commonFeatures.description && (
                <p className="text-xl text-emerald-200">{commonFeatures.description}</p>
              )}
            </div>
            
            <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-8">
              {commonItems.map((item, index) => {
                const Icon = resolveIcon(item.icon, 'Settings');
                const lines = String(item.label || '').split('\n');
                return (
                  <div key={index} className="text-center">
                    <div className="w-24 h-24 bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center mx-auto mb-4 hover:bg-white/20 transition-all">
                      <Icon className="w-12 h-12 text-white" />
                    </div>
                    <p className="text-white text-sm">
                      {lines.map((line, idx) => (
                        <React.Fragment key={idx}>
                          {line}
                          {idx !== lines.length - 1 && <br />}
                        </React.Fragment>
                      ))}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Yüzlerce Uygulama, Tek Tıkla Kuruluma Hazır */}
      <section 
        className="py-20 bg-gray-50"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {appsSection.title}
            </h2>
            {appsSection.description && (
              <p className="text-xl text-gray-600">
                {appsSection.description}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center">
            <div className="lg:col-span-2">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {(appsSection.apps || []).map((app, index) => {
                  const Icon = resolveIcon(app.icon, 'Code');
                  return (
                    <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all text-center">
                      <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-green-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <p className="font-semibold text-gray-900">{app.name}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="text-center lg:text-left">
              {(appsSection.paragraphs || []).map((paragraph, index, arr) => (
                <p
                  key={index}
                  className={`text-lg text-gray-600 ${
                    index !== arr.length - 1 ? 'mb-6' : ''
                  }`}
                >
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Web Hosting Info Sections */}
      <section 
        className="py-16 bg-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">
                <span className="text-emerald-600">{infoSectionOne.highlight}</span>{' '}
                {infoSectionOne.title}
              </h2>
              <div>
                {infoSectionOneParagraphs.map((paragraph, index) => (
                  <p
                    key={index}
                    className={`text-lg text-gray-600 ${
                      index !== infoSectionOneParagraphs.length - 1 ? 'mb-4' : ''
                    }`}
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg overflow-hidden">
              <MediaDisplay
                src={infoSectionOne.image_url || 'https://via.placeholder.com/600x400/10B981/FFFFFF?text=Web+Hosting'}
                alt={infoSectionOne.title || 'Web Hosting'}
                className="w-full h-full object-cover"
                videoProps={{ controls: true }}
                fallbackSrc="https://via.placeholder.com/600x400/10B981/FFFFFF?text=Web+Hosting"
              />
            </div>
          </div>
        </div>
      </section>

      <section 
        className="py-16 bg-gray-50"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-8 text-center lg:text-left">
                {(() => {
                  const Icon = resolveIcon(infoSectionTwoCard?.icon, 'TrendingUp');
                  return <Icon className="w-16 h-16 text-green-600 mb-4 mx-auto lg:mx-0" />;
                })()}
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  {infoSectionTwoCard?.title}
                </h3>
                {infoSectionTwoCardDescription.map((paragraph, index) => (
                  <p
                    key={index}
                    className={`text-gray-700 ${
                      index !== infoSectionTwoCardDescription.length - 1 ? 'mb-4' : ''
                    }`}
                  >
                    {paragraph}
                  </p>
                ))}
                <ul className="space-y-2">
                  {infoSectionTwoCardBullets.map((bullet, index) => (
                    <li key={index} className="flex items-center justify-center lg:justify-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      <span className="text-gray-700">{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="order-1 lg:order-2 text-center lg:text-left">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">
                <span className="text-emerald-600">{infoSectionTwo.highlight}</span>{' '}
                {infoSectionTwo.title}
              </h2>
              <div>
                {infoSectionTwoParagraphs.map((paragraph, index) => (
                  <p
                    key={index}
                    className={`text-lg text-gray-600 ${
                      index !== infoSectionTwoParagraphs.length - 1 ? 'mb-4' : ''
                    }`}
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hosting Blog Yazıları */}
      <section 
        className="py-20 bg-gray-50"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {blogsSection.title}
            </h2>
            {blogsSection.description && (
              <p className="text-xl text-gray-600">
                {blogsSection.description}
              </p>
            )}
          </div>

          {hostingBlogs.length === 0 ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600 mx-auto mb-3"></div>
              <p className="text-gray-600">Blog yazıları yükleniyor...</p>
            </div>
          ) : (
            <>
              <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                {hostingBlogs.map((blog, index) => {
                  const hasValidImage = blog.image && 
                    !blog.image.includes('placeholder');
                  
                  return (
                    <Card key={blog.id} className="overflow-hidden border-emerald-100 hover:shadow-lg transition-shadow">
                      {hasValidImage ? (
                        <div className="aspect-[16/9] bg-emerald-50 overflow-hidden">
                          <MediaDisplay
                            src={blog.image}
                            alt={blog.title}
                            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                            videoProps={{ controls: true }}
                            imgProps={{
                              loading: "lazy",
                              onError: (e) => {
                                e.target.style.display = 'none';
                                e.target.parentElement.innerHTML = '<div class="w-full h-full bg-gradient-to-br from-emerald-500 to-green-700 flex items-center justify-center"><svg class="w-16 h-16 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                              }
                            }}
                          />
                        </div>
                      ) : (
                        <div className="aspect-[16/9] bg-gradient-to-br from-emerald-500 to-green-700 flex items-center justify-center">
                          <svg className="w-16 h-16 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                          </svg>
                        </div>
                      )}
                      <CardContent className="p-5 space-y-3">
                        <div className="inline-flex items-center px-3 py-1 bg-emerald-50 text-emerald-600 text-xs font-semibold rounded-full">
                          <BookOpen className="w-3.5 h-3.5 mr-1" />
                          {blog.category || 'Hosting'}
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">{blog.title}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{blog.excerpt || 'Detaylı bilgi için blog yazısını okuyun.'}</p>
                        <button
                          onClick={() => window.location.href = `/blog/${blog.slug}`}
                          className="inline-flex items-center text-sm font-semibold text-emerald-600 hover:text-emerald-700"
                        >
                          Yazıyı Oku
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <div className="text-center mt-8">
                <Link to="/blog">
                  <Button size="lg" className="bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white">
                    Tüm İçerikleri Göster
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Sıkça Sorulan Sorular */}
      <section 
        className="py-20 bg-gray-50"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {faqSection.title}
            </h2>
            {faqSection.description && (
              <p className="text-xl text-gray-600">
                {faqSection.description}
              </p>
            )}
          </div>

          <div className="max-w-4xl mx-auto">
            {faqsLoading ? (
              <div className="text-center p-8 bg-white rounded-xl">
                <p className="text-gray-500">Yükleniyor...</p>
              </div>
            ) : faqs.length === 0 ? (
              <div className="text-center p-8 bg-white rounded-xl">
                <p className="text-gray-500">Bu kategori için soru bulunamadı.</p>
              </div>
            ) : (
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((f, idx) => (
                  <AccordionItem key={idx} value={`item-${idx}`}>
                    <AccordionTrigger className="text-left">
                      <div className="flex items-center">
                        <HelpCircle className="w-5 h-5 mr-3 text-emerald-600" />
                        {f.question}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      {f.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        </div>
      </section>

      {/* İletişim Bölümü */}
      <section 
        className="py-20 bg-gradient-to-r from-emerald-600 to-green-600 text-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-4xl font-bold mb-4">{contactSection.title}</h2>
            {contactSection.description && (
              <p className="text-xl text-emerald-100 mb-8 max-w-3xl mx-auto">
                {contactSection.description}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              {contactSection.primary_button?.text && (
                <a
                  href={contactSection.primary_button.link || '/iletisim'}
                  className="inline-flex items-center px-8 py-4 bg-white text-emerald-600 rounded-lg hover:bg-gray-100 font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  {(() => {
                    const PrimaryIcon = resolveIcon(contactSection.primary_button.icon, 'Mail');
                    return <PrimaryIcon className="w-5 h-5 mr-2" />;
                  })()}
                  {contactSection.primary_button.text}
                </a>
              )}

              {contactSection.secondary_button?.text && (
                <a
                  href={
                    contactSection.secondary_button.action === 'whatsapp'
                      ? `https://api.whatsapp.com/send/?phone=${encodeURIComponent(
                          whatsappNumber
                        )}&text=${encodeURIComponent(
                          contactSection.secondary_button?.prefill_message ||
                            'Merhaba, hosting hizmetleriniz hakkında bilgi almak istiyorum.'
                        )}`
                      : contactSection.secondary_button.link || '#'
                  }
                  target={
                    contactSection.secondary_button.action === 'whatsapp' ? '_blank' : undefined
                  }
                  rel={
                    contactSection.secondary_button.action === 'whatsapp'
                      ? 'noopener noreferrer'
                      : undefined
                  }
                  className="inline-flex items-center px-8 py-4 bg-green-700 text-white rounded-lg hover:bg-green-800 font-semibold text-lg transition-all duration-300 transform hover:scale-105 border-2 border-white shadow-lg"
                >
                  {(() => {
                    const SecondaryIcon = resolveIcon(
                      contactSection.secondary_button.icon,
                      'MessageCircle'
                    );
                    return <SecondaryIcon className="w-5 h-5 mr-2" />;
                  })()}
                  {contactSection.secondary_button.text}
                </a>
              )}
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
              {contactSection.highlights.map((highlight, index) => {
                const HighlightIcon = resolveIcon(highlight.icon, 'Clock');
                return (
                  <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                    <HighlightIcon className="w-12 h-12 mx-auto mb-4" />
                    <h3 className="font-semibold text-lg mb-2">{highlight.title}</h3>
                    {highlight.description && (
                      <p className="text-emerald-100">{highlight.description}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HostingPage;
